<!-- src/components/MessageCenter.vue -->
<template>
    <div class="message-center" ref="messageCenter">
        <div class="message-icon" @click="toggleMessages">
            <i class="fas fa-bell" :class="{ 'animate-bell': unreadCount > 0 }"></i>
            <span v-if="unreadCount > 0" class="badge">{{ unreadCount }}</span>
        </div>

        <transition name="slide-fade">
            <div v-if="showMessages" class="message-dropdown" @click.stop>
                <div class="message-header">
                    <h3>消息中心</h3>
                    <button v-if="unreadCount > 0" @click="markAllRead" :disabled="markingAllAsRead">
                        <span v-if="markingAllAsRead">
                            <i class="fas fa-spinner fa-spin"></i> 处理中...
                        </span>
                        <span v-else>全部标记已读</span>
                    </button>
                </div>

                <div class="message-list-container" ref="messageList" @scroll="handleScroll">
                    <div class="message-list">
                        <div v-if="messages.length === 0 && !isFetchingMessages" class="empty-message">
                            暂无消息
                        </div>

                        <div v-for="msg in messages" :key="msg.message_id"
                             class="message-item" :class="{ unread: msg.read_status === 0 }">
                            <div class="message-sender">
                                <i class="fas fa-user"></i>
                                {{ formatSender(msg.sender_id) }}
                            </div>
                            <div class="message-content">{{ msg.content }}</div>
                            <div class="message-time">
                                {{ formatTime(msg.timestamp) }}
                                <button v-if="msg.read_status === 0"
                                        @click="markRead(msg.message_id)"
                                        class="mark-read-btn"
                                        :disabled="markingSingleAsRead === msg.message_id">
                                    <span v-if="markingSingleAsRead === msg.message_id">
                                        <i class="fas fa-spinner fa-spin"></i> 处理中
                                    </span>
                                    <span v-else>标记已读</span>
                                </button>
                            </div>
                        </div>
                        <!-- 加载更多指示器 -->
                        <div v-if="isFetchingMessages" class="loading-indicator">
                            <i class="fas fa-spinner fa-spin"></i> 加载中...
                        </div>
                        <div v-if="!hasMoreMessages && messages.length > 0" class="no-more-messages">
                            没有更多消息了
                        </div>
                    </div>
                </div>

                <div class="message-footer">
                    <button v-if="userIsAdmin" @click="openSendMessage" :disabled="sendingMessage">
                        <span v-if="sendingMessage">
                            <i class="fas fa-spinner fa-spin"></i> 处理中...
                        </span>
                        <span v-else>发送消息</span>
                    </button>
                </div>
            </div>
        </transition>

        <transition name="fade">
            <div v-if="showSendModal" class="modal-overlay" @click="closeModal">
                <div class="message-modal" @click.stop>
                    <div class="modal-header">
                        <h3>发送消息</h3>
                        <button @click="closeModal"><i class="fas fa-times"></i></button>
                    </div>

                    <div class="modal-body">
                        <div class="form-group">
                            <label>接收者:</label>
                            <div class="receiver-select-wrapper">
                                <input type="text"
                                       v-model="userSearch"
                                       placeholder="搜索用户（邮箱、UID、昵称）"
                                       class="user-search-input"
                                       @focus="isSearchFocused = true" />
                                <div v-if="isSearchFocused" class="search-results">
                                    <div class="search-result-item all-users"
                                         :class="{ selected: receiverId === 'all' }"
                                         @click="selectAllUsers">
                                        <i class="fas fa-users"></i> 发送给所有用户
                                    </div>
                                    <div v-for="user in filteredUsers"
                                         :key="user.uid"
                                         class="search-result-item"
                                         :class="{ selected: receiverId === user.uid }"
                                         @click="selectUser(user)">
                                        <div class="user-email">{{ user.email }}</div>
                                        <div class="user-details">
                                            <span v-if="user.nickname">{{ user.nickname }}</span>
                                            <span class="user-id">ID: {{ user.uid }}</span>
                                        </div>
                                    </div>
                                    <div v-if="filteredUsers.length === 0" class="no-results">
                                        未找到匹配用户
                                    </div>
                                </div>
                                <div v-else class="selected-user" v-show="receiverId !== 'all'">
                                    <div class="user-email">{{ selectedUser?.email || '' }}</div>
                                    <div class="user-details">
                                        <span v-if="selectedUser?.nickname">{{ selectedUser.nickname }}</span>
                                        <span class="user-id">ID: {{ selectedUser?.uid || '' }}</span>
                                    </div>
                                </div>
                                <div v-if="!isSearchFocused" class="select-all"
                                     :class="{ selected: receiverId === 'all' }"
                                     @click="selectAllUsers">
                                    <i class="fas fa-users"></i> 发送给所有用户
                                </div>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>消息内容:</label>
                            <textarea v-model="messageContent" placeholder="输入消息内容..."></textarea>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="sendMessage" :disabled="!messageContent || sendingMessage">
                            <span v-if="sendingMessage">
                                <i class="fas fa-spinner fa-spin"></i> 发送中...
                            </span>
                            <span v-else>发送</span>
                        </button>
                        <button @click="closeModal">取消</button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
    import { useStore } from 'vuex';
    import axios from '@/api';
    import moment from 'moment';

    export default {
        name: 'MessageCenter',
        setup() {
            const store = useStore();
            const messageCenter = ref(null);
            const showMessages = ref(false);
            const showSendModal = ref(false);
            const receiverId = ref('all');
            const messageContent = ref('');
            const userSearch = ref('');
            const isSearchFocused = ref(false);
            const allUsers = ref([]);
            const filteredUsers = ref([]);
            const messageList = ref(null);

            // 加载状态
            const markingAllAsRead = ref(false);
            const markingSingleAsRead = ref(null);
            const sendingMessage = ref(false);
            const fetchingUsers = ref(false);

            // 计算属性
            const messages = computed(() => store.state.messages);
            const unreadCount = computed(() => store.state.unreadMessageCount);
            const isFetchingMessages = computed(() => store.state.isFetchingMessages);
            const hasMoreMessages = computed(() => store.state.hasMoreMessages);

            const userIsAdmin = computed(() => store.getters.isAdmin);
            const currentUserId = computed(() => store.state.user?.uid || '');

            // 滚动处理函数
            const handleScroll = () => {
                if (!messageList.value) return;

                const container = messageList.value;
                const scrollPosition = container.scrollTop + container.clientHeight;
                const scrollHeight = container.scrollHeight;

                // 当滚动到距离底部50px以内时加载更多
                if (scrollHeight - scrollPosition <= 50) {
                    loadMoreMessages();
                }
            };

            // 加载更多消息
            const loadMoreMessages = async () => {
                if (!isFetchingMessages.value && hasMoreMessages.value) {
                    await store.dispatch('fetchMessages', true);
                }
            };


            // 格式化发送者显示
            const formatSender = (senderId) => {
                if (senderId === 'system') return '系统通知';

                const user = allUsers.value.find(u => u.uid === senderId);
                if (user) {
                    return user.nickname ? `${user.nickname} (${user.email})` : user.email;
                }

                return `用户: ${senderId}`;
            };

            // 格式化用户显示
            const formatUserDisplay = (user) => {
                if (user.nickname) {
                    return `${user.nickname} (${user.email})`;
                }
                return user.email;
            };

            // 格式化时间
            const formatTime = (timestamp) => {
                return moment.utc(timestamp).format('YYYY-MM-DD HH:mm');
                //return moment(timestamp).format('YYYY-MM-DD HH:mm');
            };

            // 打开消息中心时加载第一页
            const toggleMessages = async () => {
                if (!showMessages.value) {
                    // 打开时重置为第一页
                    await store.dispatch('fetchMessages', false);
                }
                showMessages.value = !showMessages.value;
            };

            // 标记单条消息已读
            const markRead = async (messageId) => {
                markingSingleAsRead.value = messageId;
                try {
                    await axios.post('/api/MarkMessagesRead', {
                        uid: currentUserId.value,
                        message_ids: [messageId]
                    });
                    store.commit('markMessageRead', messageId);
                } catch (error) {
                    console.error('标记消息已读失败:', error);
                } finally {
                    markingSingleAsRead.value = null;
                }
            };

            // 标记所有消息已读
            const markAllRead = async () => {
                markingAllAsRead.value = true;
                try {
                    await axios.post('/api/MarkMessagesRead', {
                        uid: currentUserId.value,
                        message_ids: 'all'
                    });
                    store.commit('markAllMessagesRead');
                } catch (error) {
                    console.error('标记全部已读失败:', error);
                } finally {
                    markingAllAsRead.value = false;
                }
            };

            // 过滤用户列表
            const filterUsers = () => {
                if (!userSearch.value) {
                    filteredUsers.value = [...allUsers.value];
                    return;
                }

                const searchTerm = userSearch.value.toLowerCase();
                filteredUsers.value = allUsers.value.filter(user => {
                    return (
                        (user.email && user.email.toLowerCase().includes(searchTerm)) ||
                        (user.uid && user.uid.toLowerCase().includes(searchTerm)) ||
                        (user.nickname && user.nickname.toLowerCase().includes(searchTerm))
                    );
                });
            };

            // 获取所有用户（仅管理员）
            const fetchAllUsers = async () => {
                if (!userIsAdmin.value) return;
                fetchingUsers.value = true;
                try {
                    const response = await axios.post('/api/UserList', {
                        uid: currentUserId.value
                    });
                    allUsers.value = response.data.data || [];
                    filterUsers();
                } catch (error) {
                    console.error('获取用户列表失败:', error);
                } finally {
                    fetchingUsers.value = false;
                }
            };

            // 选择所有用户
            const selectAllUsers = () => {
                receiverId.value = 'all';
                isSearchFocused.value = false;
                userSearch.value = '';
            };

            // 选择用户
            const selectUser = (user) => {
                receiverId.value = user.uid;
                userSearch.value = '';
                isSearchFocused.value = false;
            };

            const selectedUser = computed(() => {
                if (receiverId.value === 'all') return null;
                return allUsers.value.find(user => user.uid === receiverId.value);
            });

            // 打开发送消息模态框
            const openSendMessage = async () => {
                if (userIsAdmin.value) {
                    sendingMessage.value = true;
                    await fetchAllUsers();
                    sendingMessage.value = false;
                    showSendModal.value = true;
                    userSearch.value = '';
                    isSearchFocused.value = false;
                }
            };

            // 关闭模态框
            const closeModal = () => {
                showSendModal.value = false;
                receiverId.value = 'all';
                messageContent.value = '';
                userSearch.value = '';
                isSearchFocused.value = false;
            };

            // 发送消息
            const sendMessage = async () => {
                sendingMessage.value = true;
                try {
                    await axios.post('/api/CreateMessage', {
                        uid: currentUserId.value,
                        receiver_id: receiverId.value,
                        content: messageContent.value
                    });

                    // 刷新消息
                    await store.dispatch('fetchMessages');

                    closeModal();
                } catch (error) {
                    console.error('发送消息失败:', error);
                } finally {
                    sendingMessage.value = false;
                }
            };

            // 点击外部关闭消息中心
            const handleClickOutside = (event) => {
                // 关闭消息中心
                if (showMessages.value && messageCenter.value &&
                    !messageCenter.value.contains(event.target)) {
                    showMessages.value = false;
                }

                // 关闭搜索下拉框
                if (isSearchFocused.value && !event.target.closest('.receiver-select-wrapper')) {
                    isSearchFocused.value = false;
                }
            };

            // 在挂载时添加滚动监听
            onMounted(() => {
                if (messageList.value) {
                    messageList.value.addEventListener('scroll', handleScroll);
                }
                document.addEventListener('click', handleClickOutside);
            });

            // 在卸载时移除滚动监听
            onUnmounted(() => {
                if (messageList.value) {
                    messageList.value.removeEventListener('scroll', handleScroll);
                }
                document.removeEventListener('click', handleClickOutside);
            });

            // 监听搜索词变化
            watch(userSearch, () => {
                filterUsers();
            });

            return {
                messageCenter,
                showMessages,
                showSendModal,
                receiverId,
                messageContent,
                userSearch,
                isSearchFocused,
                messages,
                unreadCount,
                userIsAdmin,
                allUsers,
                filteredUsers,
                toggleMessages,
                markRead,
                markAllRead,
                openSendMessage,
                closeModal,
                sendMessage,
                selectUser,
                formatTime,
                formatSender,
                formatUserDisplay,
                selectedUser,
                selectAllUsers,
                markingAllAsRead,
                markingSingleAsRead,
                sendingMessage,
                messageList,
                isFetchingMessages,
                hasMoreMessages,
                handleScroll
            };
        }
    };
</script>

<style scoped>
    /* 添加加载指示器样式 */
    .loading-indicator {
        text-align: center;
        padding: 15px;
        color: #7f8c8d;
        font-size: 0.9rem;
    }

    .no-more-messages {
        text-align: center;
        padding: 15px;
        color: #7f8c8d;
        font-size: 0.9rem;
        border-top: 1px solid #eee;
    }

    /* 确保消息列表容器可滚动 */
    .message-list-container {
        flex: 1;
        overflow-y: auto;
    }

    .message-center {
        position: relative;
        margin-right: 20px;
    }

    .message-icon {
        position: relative;
        cursor: pointer;
        font-size: 1.5rem;
        color: #ecf0f1;
        padding: 8px;
        border-radius: 50%;
        transition: background-color 0.3s, transform 0.3s;
    }

        .message-icon:hover {
            background-color: rgba(255, 255, 255, 0.1);
            transform: scale(1.1);
        }

    .badge {
        position: absolute;
        top: -5px;
        right: -5px;
        background-color: #e74c3c;
        color: white;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.7rem;
        font-weight: bold;
        animation: pulse 2s infinite;
    }

    @keyframes pulse {
        0% {
            transform: scale(1);
        }

        50% {
            transform: scale(1.1);
        }

        100% {
            transform: scale(1);
        }
    }

    /* 消息中心动画效果 */
    .slide-fade-enter-active {
        transition: all 0.3s ease-out;
    }

    .slide-fade-leave-active {
        transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
    }

    .slide-fade-enter-from,
    .slide-fade-leave-to {
        transform: translateY(-10px);
        opacity: 0;
    }

    /* 模态框动画效果 */
    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.3s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    .message-dropdown {
        position: absolute;
        top: 100%;
        right: 0;
        width: 350px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        z-index: 100;
        max-height: 500px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        transform-origin: top right;
    }

    .message-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px;
        border-bottom: 1px solid #eee;
        background: #2c3e50;
        color: white;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        flex-shrink: 0;
    }

        .message-header h3 {
            margin: 0;
            font-size: 1.1rem;
            color: white; /* 确保标题可见 */
        }

        .message-header button {
            background: #3498db;
            color: white;
            border: none;
            padding: 5px 10px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.9rem;
            transition: background-color 0.3s;
            min-width: 100px;
        }

            .message-header button:hover:not(:disabled) {
                background: #2980b9;
            }

            .message-header button:disabled {
                background: #95a5a6;
                cursor: not-allowed;
            }

    .message-list-container {
        flex: 1;
        overflow-y: auto;
    }

    .message-list {
        padding: 10px;
    }

    .empty-message {
        text-align: center;
        padding: 20px;
        color: #7f8c8d;
    }

    .message-item {
        padding: 12px;
        border-bottom: 1px solid #eee;
        transition: all 0.3s ease;
        word-break: break-word;
    }

        .message-item.unread {
            background-color: #f8f9fa;
            border-left: 3px solid #3498db;
        }

        .message-item:hover {
            background-color: #f1f8ff;
            transform: translateY(-2px);
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }

    .message-sender {
        font-weight: bold;
        color: #2c3e50;
        display: flex;
        align-items: center;
        gap: 5px;
        margin-bottom: 5px;
    }

    .message-content {
        color: #34495e;
        margin-bottom: 8px;
        line-height: 1.4;
        word-break: break-word;
    }

    .message-time {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 0.8rem;
        color: #7f8c8d;
    }

    .mark-read-btn {
        background: transparent;
        border: 1px solid #3498db;
        color: #3498db;
        padding: 3px 8px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 0.75rem;
        transition: all 0.2s;
        min-width: 80px;
    }

        .mark-read-btn:hover:not(:disabled) {
            background: #3498db;
            color: white;
            transform: scale(1.05);
        }

        .mark-read-btn:disabled {
            cursor: not-allowed;
            opacity: 0.7;
        }

    .message-footer {
        padding: 10px;
        border-top: 1px solid #eee;
        text-align: right;
        flex-shrink: 0;
    }

        .message-footer button {
            background: #2ecc71;
            color: white;
            border: none;
            padding: 8px 15px;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.3s, transform 0.2s;
            min-width: 100px;
        }

            .message-footer button:hover:not(:disabled) {
                background: #27ae60;
                transform: translateY(-2px);
            }

            .message-footer button:disabled {
                background: #95a5a6;
                cursor: not-allowed;
            }

    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
    }

    .message-modal {
        background: white;
        border-radius: 8px;
        width: 500px;
        max-width: 90%;
        box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
        overflow-x: hidden;
        transform-origin: center;
        animation: modalAppear 0.4s ease-out;
    }

    @keyframes modalAppear {
        0% {
            transform: scale(0.95);
            opacity: 0;
        }

        100% {
            transform: scale(1);
            opacity: 1;
        }
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 20px;
        border-bottom: 1px solid #eee;
        background: #2c3e50;
        color: white;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
    }

        .modal-header h3 {
            margin: 0;
            color: white; /* 确保标题可见 */
        }

        .modal-header button {
            background: none;
            border: none;
            color: white;
            font-size: 1.2rem;
            cursor: pointer;
            transition: transform 0.2s;
        }

            .modal-header button:hover {
                transform: rotate(90deg);
            }

    .modal-body {
        padding: 20px;
    }

    .form-group {
        margin-bottom: 15px;
    }

        .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: #2c3e50;
        }

        .form-group textarea {
            width: 100%;
            padding: 10px;
            border: 1px solid #ddd;
            border-radius: 6px;
            font-family: inherit;
            min-height: 120px;
            resize: vertical;
            transition: border-color 0.3s;
        }

            .form-group textarea:focus {
                border-color: #3498db;
                outline: none;
                box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
            }

    .receiver-select-wrapper {
        position: relative;
        border: 1px solid #ddd;
        border-radius: 6px;
        padding: 5px;
        background: #fff;
        transition: border-color 0.3s;
    }

        .receiver-select-wrapper:focus-within {
            border-color: #3498db;
            box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
        }

    .user-search-input {
        width: 100%;
        padding: 10px;
        border: none;
        border-radius: 4px;
        font-size: 0.9rem;
        background: transparent;
    }

        .user-search-input:focus {
            outline: none;
        }

    .search-results {
        position: absolute;
        top: 100%;
        left: -1px;
        right: -1px;
        max-height: 200px;
        overflow-y: auto;
        background: white;
        border: 1px solid #ddd;
        border-top: none;
        border-radius: 0 0 6px 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        z-index: 10;
        margin-top: 5px;
    }

    .search-result-item {
        padding: 10px 15px;
        cursor: pointer;
        transition: background-color 0.2s;
        font-size: 0.9rem;
        border-bottom: 1px solid #f0f0f0;
    }

        .search-result-item:last-child {
            border-bottom: none;
        }

        .search-result-item:hover {
            background-color: #f1f8ff;
        }

        .search-result-item.selected {
            background-color: #e3f2fd;
            border-left: 3px solid #3498db;
        }

    .all-users {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 500;
        color: #1976d2;
        background: #e3f2fd;
    }

    .user-email {
        font-weight: 500;
        color: #2c3e50;
    }

    .user-details {
        display: flex;
        justify-content: space-between;
        font-size: 0.8rem;
        color: #7f8c8d;
        margin-top: 3px;
    }

    .user-id {
        font-family: monospace;
    }

    .selected-user {
        padding: 10px;
        background: #f8f9fa;
        border-radius: 4px;
        margin-top: 8px;
        cursor: pointer;
        transition: background-color 0.2s;
    }

        .selected-user:hover {
            background: #e9ecef;
        }

    .select-all {
        padding: 10px;
        background: #e3f2fd;
        border-radius: 4px;
        margin-top: 8px;
        cursor: pointer;
        transition: background-color 0.2s;
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 500;
        color: #1976d2;
    }

        .select-all:hover {
            background: #bbdefb;
        }

        .select-all.selected {
            background: #bbdefb;
            border-left: 3px solid #3498db;
        }

    .no-results {
        padding: 10px 15px;
        color: #7f8c8d;
        font-size: 0.9rem;
        text-align: center;
    }

    .modal-footer {
        padding: 15px 20px;
        border-top: 1px solid #eee;
        text-align: right;
    }

        .modal-footer button {
            padding: 8px 20px;
            border-radius: 6px;
            cursor: pointer;
            margin-left: 10px;
            font-weight: 500;
            transition: all 0.3s;
            min-width: 100px;
        }

            .modal-footer button:first-child {
                background: #3498db;
                color: white;
                border: none;
            }

                .modal-footer button:first-child:hover:not(:disabled) {
                    background: #2980b9;
                    transform: translateY(-2px);
                }

                .modal-footer button:first-child:disabled {
                    background: #bdc3c7;
                    cursor: not-allowed;
                    transform: none;
                }

            .modal-footer button:last-child {
                background: #e0e0e0;
                border: none;
            }

                .modal-footer button:last-child:hover {
                    background: #d0d0d0;
                    transform: translateY(-2px);
                }

    /* 加载动画 */
    .fa-spinner {
        margin-right: 5px;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(360deg);
        }
    }

    .animate-bell {
        animation: bellShake 5s ease-in-out infinite;
    }

    @keyframes bellShake {
        0% {
            transform: rotate(0) scale(1);
        }

        3% {
            transform: rotate(-25deg) scale(1.2);
        }

        6% {
            transform: rotate(25deg) scale(1.4);
        }

        9% {
            transform: rotate(-20deg) scale(1.3);
        }

        12% {
            transform: rotate(20deg) scale(1.4);
        }

        15% {
            transform: rotate(-10deg) scale(1.2);
        }

        18% {
            transform: rotate(10deg) scale(1.1);
        }

        21% {
            transform: rotate(0) scale(1);
        }
    }
    /* 移动端适配：消息中心改为居中弹窗 */
    @media (max-width: 768px) {
        .message-dropdown {
            position: fixed;
            top: 70px;
            left: 5vw;
/*            height: 50%;
            right: 50%;*/
            width: 90vw;
            /*            transform: translate(-50%, -50%);
            width: 90%;
            max-width: 360px;
            max-height: 70vh;
            z-index: 9999;
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);*/
        }

    }
</style>